/**
 * PLBenchmarkComparison - Compare P&L metrics against industry benchmarks
 * 
 * Displays a visual comparison of the business's expense ratios and margins
 * against industry norms. Shows deviation levels, health score, and forensic implications.
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  RefreshCw,
  Target,
  Building2,
  PieChart
} from 'lucide-react';
import api from '../services/api';

interface MetricComparison {
  metric_key: string;
  metric_name: string;
  description: string;
  business_value: number;
  benchmark_low: number;
  benchmark_mid: number;
  benchmark_high: number;
  unit: string;
  deviation_percent: number;
  deviation_level: string;
  percentile: number;
  explanation: string;
  claim_impact: string;
}

interface BenchmarkSummary {
  total_metrics: number;
  favorable_count: number;
  normal_count: number;
  high_deviation_count: number;
  health_score: number;
  overall_assessment: string;
}

interface BenchmarkResult {
  success: boolean;
  industry: string;
  industry_key: string;
  totals?: {
    revenue: number;
    cogs: number;
    operating_expenses: number;
    operating_income: number;
  };
  comparisons: MetricComparison[];
  summary?: BenchmarkSummary;
  forensic_implications: string[];
  error?: string;
}

interface PLBenchmarkComparisonProps {
  reportId: string;
  businessType?: string;
}

export default function PLBenchmarkComparison({ reportId, businessType }: PLBenchmarkComparisonProps) {
  const [result, setResult] = useState<BenchmarkResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadBenchmark();
  }, [reportId]);

  const loadBenchmark = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try cached first
      try {
        const cachedResponse = await api.get(`/api/v1/pl-benchmark/cached/${reportId}`);
        if (cachedResponse.data?.success && cachedResponse.data?.cached) {
          setResult(cachedResponse.data);
          setLoading(false);
          return;
        }
      } catch (e) {
        // No cache, continue to fetch
      }
      
      // Fetch fresh comparison
      const response = await api.get(`/api/v1/pl-benchmark/compare/${reportId}`);
      setResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load benchmark comparison');
    } finally {
      setLoading(false);
    }
  };

  const getDeviationColor = (level: string) => {
    switch (level) {
      case 'favorable': return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' };
      case 'normal': return { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' };
      case 'moderate': return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' };
      case 'high': return { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' };
      case 'critical': return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' };
    }
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading benchmark comparison...</span>
        </div>
      </div>
    );
  }

  if (error || !result?.success) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-6 h-6 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Industry Benchmark Comparison</h3>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-600">
            <Info className="w-5 h-5" />
            <span>{result?.error || error || 'No P&L data available for benchmark comparison'}</span>
          </div>
        </div>
      </div>
    );
  }

  const { summary, comparisons, forensic_implications, industry, totals } = result;

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Industry Benchmark Comparison</h3>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Building2 className="w-4 h-4" />
                <span>{industry}</span>
              </div>
            </div>
          </div>
          <button
            onClick={loadBenchmark}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh comparison"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="grid grid-cols-4 gap-4">
            {/* Health Score */}
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke="currentColor"
                    strokeWidth="6"
                    fill="none"
                    className="text-gray-200"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke="currentColor"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={`${summary.health_score * 2.2} 220`}
                    className={getHealthScoreBg(summary.health_score)}
                    strokeLinecap="round"
                  />
                </svg>
                <span className={`absolute text-xl font-bold ${getHealthScoreColor(summary.health_score)}`}>
                  {summary.health_score}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-700 mt-1">Health Score</p>
            </div>

            {/* Favorable */}
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{summary.favorable_count}</div>
              <p className="text-sm text-gray-500">Favorable</p>
            </div>

            {/* Normal */}
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{summary.normal_count}</div>
              <p className="text-sm text-gray-500">Normal</p>
            </div>

            {/* High Deviation */}
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{summary.high_deviation_count}</div>
              <p className="text-sm text-gray-500">High Deviation</p>
            </div>
          </div>

          <p className="mt-4 text-sm text-gray-600 text-center">
            {summary.overall_assessment}
          </p>
        </div>
      )}

      {/* Forensic Implications */}
      {forensic_implications.length > 0 && (
        <div className="px-6 py-3 bg-amber-50 border-b border-amber-200">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Forensic Implications:</p>
              <ul className="mt-1 text-sm text-amber-700 list-disc list-inside">
                {forensic_implications.map((imp, idx) => (
                  <li key={idx}>{imp}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Metric Comparisons */}
      <div className="px-6 py-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <span>Detailed Metric Comparison ({comparisons.length} metrics)</span>
          <span className="text-blue-600">{expanded ? 'Hide' : 'Show'} Details</span>
        </button>

        {expanded && (
          <div className="mt-4 space-y-3">
            {comparisons.map((metric) => {
              const colors = getDeviationColor(metric.deviation_level);
              
              return (
                <div 
                  key={metric.metric_key}
                  className={`border rounded-lg p-4 ${colors.border}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-gray-900">{metric.metric_name}</h4>
                      <p className="text-xs text-gray-500">{metric.description}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors.bg} ${colors.text}`}>
                      {metric.deviation_level.charAt(0).toUpperCase() + metric.deviation_level.slice(1)}
                    </span>
                  </div>

                  {/* Visual Bar */}
                  <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden mt-3">
                    {/* Benchmark Range */}
                    <div 
                      className="absolute h-full bg-blue-200"
                      style={{
                        left: `${Math.max(0, (metric.benchmark_low / (metric.benchmark_high * 1.5)) * 100)}%`,
                        width: `${((metric.benchmark_high - metric.benchmark_low) / (metric.benchmark_high * 1.5)) * 100}%`
                      }}
                    />
                    {/* Benchmark Mid Line */}
                    <div 
                      className="absolute h-full w-0.5 bg-blue-500"
                      style={{
                        left: `${(metric.benchmark_mid / (metric.benchmark_high * 1.5)) * 100}%`
                      }}
                    />
                    {/* Business Value Marker */}
                    <div 
                      className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow ${
                        metric.deviation_level === 'favorable' ? 'bg-green-500' :
                        metric.deviation_level === 'normal' ? 'bg-blue-500' :
                        metric.deviation_level === 'moderate' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{
                        left: `${Math.min(95, Math.max(5, (metric.business_value / (metric.benchmark_high * 1.5)) * 100))}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                    />
                  </div>

                  {/* Values */}
                  <div className="flex justify-between mt-2 text-xs">
                    <span className="text-gray-500">
                      Industry: {metric.benchmark_low}{metric.unit} - {metric.benchmark_high}{metric.unit}
                    </span>
                    <span className="font-medium text-gray-900">
                      Your Value: {metric.business_value}{metric.unit}
                    </span>
                  </div>

                  {/* Explanation */}
                  <p className="mt-2 text-sm text-gray-600">{metric.explanation}</p>
                  
                  {/* Claim Impact */}
                  {metric.claim_impact && (
                    <p className="mt-1 text-xs text-gray-500 italic">
                      <Target className="inline w-3 h-3 mr-1" />
                      {metric.claim_impact}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Financial Totals */}
      {totals && totals.revenue > 0 && (
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <div className="grid grid-cols-4 gap-4 text-center text-sm">
            <div>
              <p className="text-gray-500">Revenue</p>
              <p className="font-semibold text-gray-900">{formatCurrency(totals.revenue)}</p>
            </div>
            <div>
              <p className="text-gray-500">COGS</p>
              <p className="font-semibold text-gray-900">{formatCurrency(totals.cogs)}</p>
            </div>
            <div>
              <p className="text-gray-500">Op. Expenses</p>
              <p className="font-semibold text-gray-900">{formatCurrency(totals.operating_expenses)}</p>
            </div>
            <div>
              <p className="text-gray-500">Op. Income</p>
              <p className={`font-semibold ${totals.operating_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(totals.operating_income)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
